import type {
  AppStatus,
  Chapter,
  Ledger,
  LogEntry,
  ProjectMetadata,
  SequelCompletedStep,
  SequelOperation,
  SequelOperationStatus,
  StoryBible,
  StoryMode,
} from '../types';
import { computeSequelOperationId, computeSequelTargetProjectId } from './sequelIdentity';
import { doc, getDoc, patchDoc, writeDoc, db } from './firestoreWrite';

export interface SequelInitContext {
  ownerUid: string;
  sourceProjectId: string;
  sourceVolume: number;
  seriesId: string;
  ledger: Ledger;
  storyBible: StoryBible;
  chapters: Chapter[];
}

function operationRef(uid: string, sourceProjectId: string, operationId: string) {
  return doc(db, 'users', uid, 'projects', sourceProjectId, 'sequelOperations', operationId);
}

function targetProjectRef(uid: string, targetProjectId: string) {
  return doc(db, 'users', uid, 'projects', targetProjectId);
}

function targetStateRef(uid: string, targetProjectId: string) {
  return doc(db, 'users', uid, 'projects', targetProjectId, 'state', 'current');
}

export function buildSequelIdentity(ctx: SequelInitContext) {
  const targetVolume = ctx.sourceVolume + 1;
  const operationId = computeSequelOperationId({
    ownerUid: ctx.ownerUid,
    sourceProjectId: ctx.sourceProjectId,
    targetVolume,
    seriesId: ctx.seriesId,
  });
  const targetProjectId = computeSequelTargetProjectId({
    ownerUid: ctx.ownerUid,
    seriesId: ctx.seriesId,
    targetVolume,
  });
  return { targetVolume, operationId, targetProjectId };
}

export function shouldRunLedgerStep(operation: SequelOperation | null): boolean {
  return !operation?.completedSteps?.includes('ledger_evolved');
}

export function shouldRunBibleStep(operation: SequelOperation | null): boolean {
  return !operation?.completedSteps?.includes('bible_evolved');
}

export function isSequelOperationTerminal(status: SequelOperationStatus): boolean {
  return status === 'READY' || status === 'FAILED' || status === 'CANCEL_REQUESTED';
}

export async function loadSequelOperation(
  uid: string,
  sourceProjectId: string,
  operationId: string,
): Promise<SequelOperation | null> {
  const snap = await getDoc(operationRef(uid, sourceProjectId, operationId));
  return snap.exists() ? (snap.data() as SequelOperation) : null;
}

export async function ensureSequelOperation(
  ctx: SequelInitContext,
): Promise<{ operation: SequelOperation; created: boolean }> {
  const { targetVolume, operationId, targetProjectId } = buildSequelIdentity(ctx);
  const ref = operationRef(ctx.ownerUid, ctx.sourceProjectId, operationId);
  const existing = await getDoc(ref);
  if (existing.exists()) {
    return { operation: existing.data() as SequelOperation, created: false };
  }
  const now = new Date().toISOString();
  const operation: SequelOperation = {
    operationId,
    sourceProjectId: ctx.sourceProjectId,
    targetVolume,
    targetProjectId,
    seriesId: ctx.seriesId,
    status: 'PENDING',
    completedSteps: [],
    createdAt: now,
    updatedAt: now,
    requestIds: [],
    resultRefs: {},
  };
  await writeDoc(ref, operation);
  return { operation, created: true };
}

export async function ensureTargetProjectStub(
  ctx: SequelInitContext,
  operation: SequelOperation,
): Promise<ProjectMetadata> {
  const ref = targetProjectRef(ctx.ownerUid, operation.targetProjectId);
  const existing = await getDoc(ref);
  const now = new Date().toISOString();
  if (existing.exists()) {
    return existing.data() as ProjectMetadata;
  }

  const metadata: ProjectMetadata = {
    id: operation.targetProjectId,
    seriesId: ctx.seriesId,
    previousVolumeId: ctx.sourceProjectId,
    sourceProjectId: ctx.sourceProjectId,
    sequelOperationId: operation.operationId,
    sequelSetupStatus: 'INITIALIZING',
    title: ctx.storyBible.title,
    genre: Array.isArray(ctx.storyBible.genre)
      ? ctx.storyBible.genre.map((g) => g.name).join(', ')
      : String(ctx.storyBible.genre || ''),
    lastUpdated: now,
    chapterCount: 0,
    mode: ctx.storyBible.mode as StoryMode,
    volume: operation.targetVolume,
    status: 'INITIALIZING' as AppStatus,
    creditTier: 'free_preview',
    rewritesUsed: 0,
    maxChaptersAllowed: 1,
    exportUnlocked: false,
    userId: ctx.ownerUid,
  };

  await writeDoc(ref, metadata);

  const seedLogs: LogEntry[] = [
    {
      id: `log_${Date.now()}`,
      timestamp: new Date().toLocaleTimeString(),
      agent: 'System',
      message: `Volume ${operation.targetVolume} initialization started from ${ctx.sourceProjectId}.`,
      type: 'action',
    },
  ];

  await writeDoc(targetStateRef(ctx.ownerUid, operation.targetProjectId), {
    chapters: [],
    currentChapterNum: 1,
    logs: seedLogs,
    sequelOperationId: operation.operationId,
    sourceProjectId: ctx.sourceProjectId,
    initializing: true,
    updatedAt: now,
  });

  return metadata;
}

async function patchOperation(
  ref: ReturnType<typeof operationRef>,
  patch: Partial<SequelOperation>,
): Promise<void> {
  await patchDoc(ref, { ...patch, updatedAt: new Date().toISOString() } as Record<string, unknown>);
}

export async function markOperationStatus(
  uid: string,
  sourceProjectId: string,
  operationId: string,
  status: SequelOperationStatus,
  extra?: Partial<SequelOperation>,
): Promise<void> {
  const ref = operationRef(uid, sourceProjectId, operationId);
  await patchOperation(ref, { status, ...extra });
}

export async function appendOperationRequestId(
  uid: string,
  sourceProjectId: string,
  operationId: string,
  requestId: string,
): Promise<void> {
  const ref = operationRef(uid, sourceProjectId, operationId);
  const snap = await getDoc(ref);
  const current = snap.exists() ? (snap.data() as SequelOperation) : null;
  const requestIds = [...(current?.requestIds || [])];
  if (!requestIds.includes(requestId)) requestIds.push(requestId);
  await patchOperation(ref, { requestIds });
}

export async function persistLedgerEvolutionResult(
  uid: string,
  ctx: SequelInitContext,
  operation: SequelOperation,
  evolvedLedger: Ledger,
  requestId?: string,
): Promise<SequelOperation> {
  const ref = operationRef(uid, ctx.sourceProjectId, operation.operationId);
  const completedSteps = Array.from(
    new Set([...(operation.completedSteps || []), 'ledger_evolved' as SequelCompletedStep]),
  );
  const requestIds = [...(operation.requestIds || [])];
  if (requestId && !requestIds.includes(requestId)) requestIds.push(requestId);

  const next: SequelOperation = {
    ...operation,
    status: 'EVOLVING_BIBLE',
    completedSteps,
    evolvedLedger,
    requestIds,
    resultRefs: {
      ...(operation.resultRefs || {}),
      evolvedLedgerPath: `users/${uid}/projects/${operation.targetProjectId}/state/current`,
    },
    updatedAt: new Date().toISOString(),
  };
  await writeDoc(ref, next, { merge: true });
  await writeDoc(
    targetStateRef(uid, operation.targetProjectId),
    { ledger: evolvedLedger, updatedAt: next.updatedAt },
    { merge: true },
  );
  return next;
}

export async function persistBibleEvolutionResult(
  uid: string,
  ctx: SequelInitContext,
  operation: SequelOperation,
  evolvedBible: StoryBible,
  requestId?: string,
): Promise<SequelOperation> {
  const ref = operationRef(uid, ctx.sourceProjectId, operation.operationId);
  const completedSteps = Array.from(
    new Set([...(operation.completedSteps || []), 'bible_evolved' as SequelCompletedStep]),
  );
  const requestIds = [...(operation.requestIds || [])];
  if (requestId && !requestIds.includes(requestId)) requestIds.push(requestId);

  const next: SequelOperation = {
    ...operation,
    status: 'READY',
    completedSteps,
    evolvedBible,
    requestIds,
    resultRefs: {
      ...(operation.resultRefs || {}),
      evolvedBiblePath: `users/${uid}/projects/${operation.targetProjectId}/state/current`,
    },
    updatedAt: new Date().toISOString(),
  };
  await writeDoc(ref, next, { merge: true });
  await writeDoc(
    targetStateRef(uid, operation.targetProjectId),
    {
      storyBible: evolvedBible,
      updatedAt: next.updatedAt,
      initializing: false,
    },
    { merge: true },
  );
  await writeDoc(
    targetProjectRef(uid, operation.targetProjectId),
    {
      sequelSetupStatus: 'READY',
      status: 'SETUP',
      lastUpdated: next.updatedAt,
      title: evolvedBible.title,
      volume: evolvedBible.volume,
    },
    { merge: true },
  );
  return next;
}

export async function markSequelUnknownOutcome(
  uid: string,
  sourceProjectId: string,
  operationId: string,
  requestId?: string,
): Promise<void> {
  await markOperationStatus(uid, sourceProjectId, operationId, 'UNKNOWN_OUTCOME', {
    errorCode: 'TIMEOUT_UNKNOWN_OUTCOME',
    ...(requestId ? { requestIds: [requestId] } : {}),
  });
}

export async function markSequelFailed(
  uid: string,
  sourceProjectId: string,
  operationId: string,
  errorCode: string,
  requestId?: string,
): Promise<void> {
  await markOperationStatus(uid, sourceProjectId, operationId, 'FAILED', {
    errorCode,
    ...(requestId ? { requestIds: requestId ? [requestId] : [] } : {}),
  });
}

export async function requestSequelCancel(
  uid: string,
  sourceProjectId: string,
  operationId: string,
): Promise<void> {
  await markOperationStatus(uid, sourceProjectId, operationId, 'CANCEL_REQUESTED', {
    errorCode: 'USER_CANCEL_REQUESTED',
  });
  const op = await loadSequelOperation(uid, sourceProjectId, operationId);
  if (op?.targetProjectId) {
    await writeDoc(
      targetProjectRef(uid, op.targetProjectId),
      { sequelSetupStatus: 'CANCEL_REQUESTED', lastUpdated: new Date().toISOString() },
      { merge: true },
    );
  }
}

export function buildReadyProjectState(
  operation: SequelOperation,
  evolvedLedger: Ledger,
  evolvedBible: StoryBible,
  seedLogs: LogEntry[],
): {
  metadata: ProjectMetadata;
  fullState: {
    id: string;
    seriesId: string;
    volume: number;
    ledger: Ledger;
    storyBible: StoryBible;
    chapters: Chapter[];
    currentChapterNum: number;
    logs: LogEntry[];
    ledgerHistory: Ledger[];
    roadmapVersions: [];
  };
} {
  const metadata: ProjectMetadata = {
    id: operation.targetProjectId,
    seriesId: operation.seriesId,
    previousVolumeId: operation.sourceProjectId,
    sourceProjectId: operation.sourceProjectId,
    sequelOperationId: operation.operationId,
    sequelSetupStatus: 'READY',
    title: evolvedBible.title,
    genre: Array.isArray(evolvedBible.genre)
      ? evolvedBible.genre.map((g) => g.name).join(', ')
      : String(evolvedBible.genre || ''),
    lastUpdated: new Date().toISOString(),
    chapterCount: 0,
    mode: evolvedBible.mode,
    volume: evolvedBible.volume,
    status: 'SETUP' as AppStatus,
    creditTier: 'free_preview',
    rewritesUsed: 0,
    maxChaptersAllowed: 1,
    exportUnlocked: false,
  };

  return {
    metadata,
    fullState: {
      id: operation.targetProjectId,
      seriesId: operation.seriesId,
      volume: evolvedBible.volume,
      ledger: evolvedLedger,
      storyBible: evolvedBible,
      chapters: [],
      currentChapterNum: 1,
      logs: seedLogs,
      ledgerHistory: [],
      roadmapVersions: [],
    },
  };
}

export function filterDashboardProjects(projects: ProjectMetadata[]): ProjectMetadata[] {
  return projects.filter((p) => p.sequelSetupStatus !== 'INITIALIZING' && p.status !== 'INITIALIZING');
}

export function findPendingSequelProject(projects: ProjectMetadata[]): ProjectMetadata | undefined {
  return projects.find(
    (p) =>
      p.sequelSetupStatus === 'INITIALIZING' ||
      p.status === 'INITIALIZING' ||
      p.sequelSetupStatus === 'CANCEL_REQUESTED',
  );
}
